import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { DRAWER_CARD_CLASS, DRAWER_CARD_TITLE_CLASS } from '../shared/styles'
import Badge from '../../ui/Badge'
import Select from '../../ui/Select/Select'
import Tooltip from '../../ui/Tooltip'
import CheckIcon from '../../ui/icons/CheckIcon'
import CopyOutlineIcon from '../../ui/icons/CopyOutlineIcon'
import EditIcon from '../../ui/icons/EditIcon'
import StatusDot from '../IssueStatuses/StatusDot'
import StatusSelect from './StatusSelect'
import { formatDate, formatDateTime } from '../../../utils/dateHelpers'
import copyToClipboard from '../../../utils/clipboard'

const FULL_NAME_KEY = 'full_name'
const STATUS_CHANGED_AT_KEY = 'status_changed_at'
const CREATED_AT_KEY = 'created_at'
const RESOURCE_URL_KEY = 'resource_url'
const SCHEDULED_AT_KEY = 'scheduled_at'

const CARD_CLASS = DRAWER_CARD_CLASS
const HEAD_CLASS = 'tw:flex tw:items-start tw:justify-between tw:gap-2 tw:mb-4'
const TITLE_CLASS = `${DRAWER_CARD_TITLE_CLASS} tw:min-w-0 tw:break-words`
const EDIT_BUTTON_CLASS = 'tw:inline-flex tw:items-center tw:justify-center tw:size-9 tw:shrink-0 '
  + 'tw:rounded! tw:border tw:border-solid tw:border-gray-300 tw:bg-white tw:cursor-pointer '
  + 'tw:text-slate-700 tw:hover:bg-gray-50 tw:hover:border-slate-300'
const STATUS_BLOCK_CLASS = 'tw:flex tw:flex-col tw:gap-1 tw:pb-4'
const STATUS_LABEL_CLASS = 'tw:flex tw:items-center tw:gap-0.5 tw:text-xs tw:font-medium tw:text-gray-900'
const REQUIRED_MARK_CLASS = 'tw:text-red-700'
const BLOCK_CLASS = 'tw:flex tw:flex-col tw:gap-1 tw:py-4 tw:border-t tw:border-solid tw:border-gray-200'
const LABEL_CLASS = 'tw:text-sm tw:text-gray-600'
const VALUE_CLASS = 'tw:text-sm tw:text-gray-900'
const DATE_CLASS = 'tw:text-sm tw:text-gray-700'
const LINK_CLASS = 'tw:text-sm tw:text-gray-900! tw:underline!'
const CHIPS_CLASS = 'tw:flex tw:flex-wrap tw:gap-1'
const COPY_BUTTON_CLASS = 'tw:inline-flex tw:items-center tw:bg-transparent tw:border-0 '
  + 'tw:cursor-pointer tw:text-gray-500 tw:p-0 tw:hover:text-gray-900'
const RESOURCE_CLASS = 'tw:flex tw:items-center tw:gap-1 tw:min-w-0'
const COPIED_CLASS = 'tw:inline-flex tw:items-center tw:gap-1 tw:text-sm tw:text-gray-900'
const CHECK_ICON_CLASS = 'tw:size-4 tw:text-green-600'
const COPY_FAILED_CLASS = 'tw:text-xs tw:text-red-700'
const TRIGGERS_CLASS = 'tw:flex tw:flex-col tw:gap-2'
const TRIGGER_CLASS = 'tw:block tw:wrap-anywhere tw:text-sm tw:text-orange-700'
const EMPTY = '--'
const EMPTY_RECIPIENTS = []
const COPY_FEEDBACK_MS = 2000
const COPY_MESSAGES = { copied: 'Copié !', failed: 'Copie impossible' }

const block = (label, content) => (
  <div key={label} className={BLOCK_CLASS}>
    <span className={LABEL_CLASS}>{label}</span>
    {content}
  </div>
)

const chips = (values, keyOf, labelOf) => {
  if (!values || values.length === 0)
    return <span className={VALUE_CLASS}>{EMPTY}</span>
  return (
    <span className={CHIPS_CLASS}>
      {values.map(value => <Badge key={keyOf(value)} variant="neutral">{labelOf(value)}</Badge>)}
    </span>
  )
}

const renderTriggers = triggers => {
  if (!triggers || triggers.length === 0)
    return <span className={VALUE_CLASS}>{EMPTY}</span>
  return (
    <div className={TRIGGERS_CLASS}>
      {triggers.map(trigger => (
        <span key={`${trigger.target}-${trigger.state}`} className={TRIGGER_CLASS}>
          <Badge variant="warning" wrap>{trigger.target}</Badge>
          {' '}
          <span>{trigger.state}</span>
        </span>
      ))}
    </div>
  )
}

const triggersLabel = triggers => triggers?.length > 1 ? 'Déclencheurs' : 'Déclencheur'

const IssueSummaryCard = ({
  issue, issueStatuses, statusValue, onStatusChange,
  recipientOptions, selectedUsers, onUsersChange, isEditing, onEdit
}) => {
  const [copyState, setCopyState] = useState(null)
  const selectedStatus = issueStatuses.find(status => status.id === statusValue) || null
  const resourceName = issue.answer?.resource?.name
  const recipients = issue.recipients || EMPTY_RECIPIENTS

  const handleCopy = useCallback(async() => {
    // A fresh object per copy restarts the feedback timer even when the outcome repeats.
    setCopyState({ status: await copyToClipboard(resourceName || '') ? 'copied' : 'failed' })
  }, [resourceName])

  useEffect(() => {
    const timer = copyState ? setTimeout(() => setCopyState(null), COPY_FEEDBACK_MS) : null
    return () => clearTimeout(timer)
  }, [copyState])

  const renderStatus = () => {
    if (isEditing) {
      return (
        <>
          <span className={STATUS_LABEL_CLASS}>
            <span>Statut</span>
            <span className={REQUIRED_MARK_CLASS}>*</span>
          </span>
          <StatusSelect
            inputId="issue-status"
            issueStatuses={issueStatuses}
            value={statusValue}
            onChange={onStatusChange}
          />
        </>
      )
    }
    if (!selectedStatus)
      return <span className={VALUE_CLASS}>{EMPTY}</span>
    return <StatusDot className={VALUE_CLASS} color={selectedStatus.color} label={selectedStatus.name} />
  }

  const renderAttribution = () => {
    if (isEditing) {
      return (
        <Select
          closeMenuOnSelect={false}
          inputId="issue-attribution"
          menuPortalTarget={document.body}
          menuPosition="fixed"
          options={recipientOptions}
          placeholder="Sélectionner des utilisateurs..."
          value={selectedUsers}
          isMulti
          onChange={onUsersChange}
        />
      )
    }
    return chips(issue.attribution, user => user.id, user => user[FULL_NAME_KEY])
  }

  const renderScheduled = () => {
    const scheduledAt = issue[SCHEDULED_AT_KEY]
    if (!scheduledAt)
      return null
    return block('Date de planification', <span className={VALUE_CLASS}>{formatDateTime(scheduledAt)}</span>)
  }

  const renderResource = () => (
    <span className={RESOURCE_CLASS}>
      <a className={LINK_CLASS} href={issue[RESOURCE_URL_KEY]} rel="noreferrer" target="_blank">
        {resourceName || EMPTY}
      </a>
      <Tooltip className="tw:inline-flex" content="Copier">
        <button
          aria-label="Copier le nom de la ressource"
          className={COPY_BUTTON_CLASS}
          type="button"
          onClick={handleCopy}
        >
          <CopyOutlineIcon className="tw:size-4" />
        </button>
      </Tooltip>
      {copyState ? (
        <span className={copyState.status === 'copied' ? COPIED_CLASS : COPY_FAILED_CLASS} role="status">
          {copyState.status === 'copied' ? <CheckIcon className={CHECK_ICON_CLASS} /> : null}
          {COPY_MESSAGES[copyState.status]}
        </span>
      ) : null}
    </span>
  )

  return (
    <section className={CARD_CLASS}>
      <div className={HEAD_CLASS}>
        <h3 className={TITLE_CLASS}>{issue.name}</h3>
        {!isEditing && (
          <Tooltip className="tw:inline-flex" content="Éditer">
            <button
              aria-label="Éditer le résumé de l’alerte"
              className={EDIT_BUTTON_CLASS}
              type="button"
              onClick={onEdit}
            >
              <EditIcon className="tw:size-5" />
            </button>
          </Tooltip>
        )}
      </div>
      <div className={STATUS_BLOCK_CLASS}>
        {renderStatus()}
        <span className={DATE_CLASS}>
          {`Mis à jour du statut : ${formatDate(issue[STATUS_CHANGED_AT_KEY])}`}
        </span>
        <span className={DATE_CLASS}>
          {`Déclenché le : ${formatDate(issue[CREATED_AT_KEY])}`}
        </span>
      </div>
      {block('Collecte', <span className={VALUE_CLASS}>{issue.collect?.name || EMPTY}</span>)}
      {renderScheduled()}
      {block('Nom de la ressource', renderResource())}
      {block(triggersLabel(issue.triggers), renderTriggers(issue.triggers))}
      {block('Attribution de l’alerte', renderAttribution())}
      {recipients.length > 0 ? block('Destinataires', chips(recipients, name => name, name => name)) : null}
      {block('Opérateurs', chips(issue.operators, name => name, name => name))}
    </section>
  )
}

IssueSummaryCard.propTypes = {
  isEditing: PropTypes.bool.isRequired,
  issue: PropTypes.shape({
    attribution: PropTypes.arrayOf(PropTypes.shape({})),
    collect: PropTypes.shape({ name: PropTypes.string }),
    name: PropTypes.string,
    operators: PropTypes.arrayOf(PropTypes.string),
    recipients: PropTypes.arrayOf(PropTypes.string),
    triggers: PropTypes.arrayOf(PropTypes.shape({
      state: PropTypes.string,
      target: PropTypes.string
    }))
  }).isRequired,
  issueStatuses: PropTypes.arrayOf(PropTypes.shape({
    color: PropTypes.string,
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  })),
  onEdit: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onUsersChange: PropTypes.func.isRequired,
  recipientOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedUsers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  statusValue: PropTypes.number
}

IssueSummaryCard.defaultProps = {
  issueStatuses: [],
  statusValue: null
}

export default IssueSummaryCard
